import type {
  CanonicalTreeLegacyGenerationProvenanceStatus,
  ExistingTreeAuditFinding,
  ExistingTreeAuditKind,
  ExistingTreeStructuralAudit,
} from './ExistingTreeStructuralAudit';

export type ExistingTreeRepairCategory =
  | 'REFERENCE_INTEGRITY'
  | 'PARENT_ROLE_REVIEW'
  | 'IDENTITY_REVIEW'
  | 'GENERATION_REVIEW'
  | 'DISPLAY_PREFERENCE';

export type ExistingTreeRequiredApproval =
  | 'TREE_OWNER'
  | 'CLAIMED_MEMBER'
  | 'IDENTITY_REVIEW'
  | 'GOVERNED_OPERATOR_REVIEW';

export interface ExistingTreeRepairPlanItem {
  category: ExistingTreeRepairCategory;
  findingKind: ExistingTreeAuditKind;
  severity: ExistingTreeAuditFinding['severity'];
  memberId: string;
  memberName: string;
  relatedIds: string[];
  summary: string;
  requiredApprovals: ExistingTreeRequiredApproval[];
  executionAllowed: false;
  automaticRepairAllowed: false;
}

export interface ExistingTreeRepairPlan {
  generatedFromFindingCount: number;
  itemCount: number;
  categories: ExistingTreeRepairCategory[];
  items: ExistingTreeRepairPlanItem[];
  executionAllowed: false;
  productionWritesAllowed: false;
}

const classifyFinding = (
  kind: ExistingTreeAuditKind,
): {
  category: ExistingTreeRepairCategory;
  requiredApprovals: ExistingTreeRequiredApproval[];
} => {
  switch (kind) {
    case 'dangling-parent-reference':
    case 'self-parent-reference':
      return {
        category: 'REFERENCE_INTEGRITY',
        requiredApprovals: [
          'TREE_OWNER',
          'GOVERNED_OPERATOR_REVIEW',
        ],
      };

    case 'excessive-parent-count':
      return {
        category: 'PARENT_ROLE_REVIEW',
        requiredApprovals: [
          'TREE_OWNER',
          'GOVERNED_OPERATOR_REVIEW',
        ],
      };

    case 'unattached-claimed-profile':
      return {
        category: 'IDENTITY_REVIEW',
        requiredApprovals: [
          'TREE_OWNER',
          'CLAIMED_MEMBER',
          'IDENTITY_REVIEW',
        ],
      };

    case 'direct-ancestor-depth-mismatch':
      return {
        category: 'GENERATION_REVIEW',
        requiredApprovals: [
          'TREE_OWNER',
          'GOVERNED_OPERATOR_REVIEW',
        ],
      };

    case 'nickname-preference-available':
      return {
        category: 'DISPLAY_PREFERENCE',
        requiredApprovals: ['CLAIMED_MEMBER'],
      };
  }
};

export function buildExistingTreeRepairPlan(
  audit: ExistingTreeStructuralAudit,
): ExistingTreeRepairPlan {
  const items = audit.findings.map((finding) => {
    const classification = classifyFinding(
      finding.kind,
    );

    return {
      category: classification.category,
      findingKind: finding.kind,
      severity: finding.severity,
      memberId: finding.memberId,
      memberName: finding.memberName,
      relatedIds: [...finding.relatedIds],
      summary: finding.summary,
      requiredApprovals: [
        ...classification.requiredApprovals,
      ],
      executionAllowed: false,
      automaticRepairAllowed: false,
    } satisfies ExistingTreeRepairPlanItem;
  });

  return {
    generatedFromFindingCount: audit.findingCount,
    itemCount: items.length,
    categories: Array.from(
      new Set(items.map((item) => item.category)),
    ),
    items,
    executionAllowed: false,
    productionWritesAllowed: false,
  };
}
/*
 * CC-TREE-LIVE-003G3L
 *
 * EXISTING_TREE_RECONCILIATION_REPAIR_ELIGIBILITY_AUTHORITY
 *
 * This authority classifies whether a known live Tree defect has enough
 * evidence to enter a successor repair-planning phase. It grants no execution
 * authority and performs no mutation.
 *
 * Identity correlation is never identity authority. Similar names, matching
 * relationship labels, timing proximity, contact correlation, structural
 * adjacency, or a browser-selected profile cannot prove that two Tree rows
 * represent the same person.
 *
 * A row with three or more parent references is not automatically corrupt.
 * Modern-family parent roles require explicit role evidence before any edge
 * can be retired.
 *
 * A direct-ancestor depth contradiction becomes planning-eligible only when
 * the governed historical provenance classifier reports exactly one
 * PROVEN_UNAMBIGUOUS_ENDPOINT.
 *
 * A reciprocal stored partnership plus a proven projection/renderer failure is
 * presentation-plane work. It is not authority to rewrite family topology.
 */
export type ExistingTreeReconciliationDefectClass =
  | 'WRONG_CANONICAL_BINDING'
  | 'DUPLICATE_PLACEHOLDER_CANDIDATE'
  | 'EXCESSIVE_PARENT_ROLE_SET'
  | 'DIRECT_ANCESTOR_DEPTH_CONTRADICTION'
  | 'PARTNERSHIP_PRESENTATION_CONTRADICTION';

export type ExistingTreeRepairPlane =
  | 'IDENTITY'
  | 'TOPOLOGY'
  | 'PRESENTATION'
  | 'NONE';

export type ExistingTreeRepairEligibilityStatus =
  | 'REPAIR_PLANNING_ELIGIBLE'
  | 'EVIDENCE_REQUIRED'
  | 'PRESENTATION_ONLY'
  | 'NO_STORED_TOPOLOGY_REPAIR';

export interface ExistingTreeReconciliationEvidence {
  readonly defectClass:
    ExistingTreeReconciliationDefectClass;

  readonly sameGraphProven:
    boolean;

  readonly identityEquivalenceProven:
    boolean;

  readonly identityCorrelationOnly:
    boolean;

  readonly claimedProfileUnattached:
    boolean;

  readonly canonicalProfileStructurallyConnected:
    boolean;

  readonly redundantRecordProven:
    boolean;

  readonly parentRoleEvidenceComplete:
    boolean;

  readonly intendedParentSetProven:
    boolean;

  readonly generationProvenanceStatus:
    CanonicalTreeLegacyGenerationProvenanceStatus;

  readonly storedPartnershipReciprocal:
    boolean;

  readonly projectionOrRendererFailureProven:
    boolean;
}

export interface ExistingTreeRepairEligibilityDecision {
  readonly defectClass:
    ExistingTreeReconciliationDefectClass;

  readonly status:
    ExistingTreeRepairEligibilityStatus;

  readonly repairPlane:
    ExistingTreeRepairPlane;

  readonly repairPlanningEligible:
    boolean;

  readonly requiresAdditionalEvidence:
    boolean;

  readonly reason:
    string;

  readonly containsPrivateIdentity:
    true;

  readonly publishable:
    false;

  readonly readOnly:
    true;

  readonly executionAllowed:
    false;

  readonly productionWritesAllowed:
    false;

  readonly automaticRepairAllowed:
    false;
}

const existingTreeRepairEligibilityDecision = (
  defectClass:
    ExistingTreeReconciliationDefectClass,
  status:
    ExistingTreeRepairEligibilityStatus,
  repairPlane:
    ExistingTreeRepairPlane,
  repairPlanningEligible:
    boolean,
  reason:
    string,
): ExistingTreeRepairEligibilityDecision =>
  Object.freeze({
    defectClass,
    status,
    repairPlane,
    repairPlanningEligible,
    requiresAdditionalEvidence:
      status === 'EVIDENCE_REQUIRED',
    reason,
    containsPrivateIdentity: true,
    publishable: false,
    readOnly: true,
    executionAllowed: false,
    productionWritesAllowed: false,
    automaticRepairAllowed: false,
  });

export function classifyExistingTreeRepairEligibility(
  input:
    ExistingTreeReconciliationEvidence,
): ExistingTreeRepairEligibilityDecision {
  if (!input.sameGraphProven) {
    return existingTreeRepairEligibilityDecision(
      input.defectClass,
      'EVIDENCE_REQUIRED',
      'NONE',
      false,
      'Repair planning fails closed until same-graph scope is proven.',
    );
  }

  switch (input.defectClass) {
    case 'WRONG_CANONICAL_BINDING':
      if (
        input.identityCorrelationOnly ||
        !input.identityEquivalenceProven ||
        !input.claimedProfileUnattached ||
        !input.canonicalProfileStructurallyConnected
      ) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'EVIDENCE_REQUIRED',
          'IDENTITY',
          false,
          'A wrong-binding repair requires governed identity-equivalence proof plus an unattached claimed row and a structurally connected canonical row. Correlation alone is never identity authority.',
        );
      }

      return existingTreeRepairEligibilityDecision(
        input.defectClass,
        'REPAIR_PLANNING_ELIGIBLE',
        'IDENTITY',
        true,
        'Identity evidence is sufficient to plan canonical rebind recovery. Execution remains separately governed.',
      );

    case 'DUPLICATE_PLACEHOLDER_CANDIDATE':
      if (
        input.identityCorrelationOnly ||
        !input.identityEquivalenceProven ||
        !input.redundantRecordProven
      ) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'EVIDENCE_REQUIRED',
          'IDENTITY',
          false,
          'Duplicate-looking placeholders remain separate identities until governed evidence proves equivalence and proves which record is redundant.',
        );
      }

      return existingTreeRepairEligibilityDecision(
        input.defectClass,
        'REPAIR_PLANNING_ELIGIBLE',
        'IDENTITY',
        true,
        'Identity equivalence and redundant-record authority are proven. A successor may plan consolidation without inferring identity.',
      );

    case 'EXCESSIVE_PARENT_ROLE_SET':
      if (
        !input.parentRoleEvidenceComplete ||
        !input.intendedParentSetProven
      ) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'EVIDENCE_REQUIRED',
          'TOPOLOGY',
          false,
          'Parent-count cardinality alone is not corruption. Biological, adoptive, step, chosen, and other governed parent roles require explicit review before any edge can change.',
        );
      }

      return existingTreeRepairEligibilityDecision(
        input.defectClass,
        'REPAIR_PLANNING_ELIGIBLE',
        'TOPOLOGY',
        true,
        'Parent-role evidence proves the intended parent set. A successor may plan a stale-state-protected topology repair.',
      );

    case 'DIRECT_ANCESTOR_DEPTH_CONTRADICTION':
      if (
        input.generationProvenanceStatus !==
          'PROVEN_UNAMBIGUOUS_ENDPOINT'
      ) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'EVIDENCE_REQUIRED',
          'TOPOLOGY',
          false,
          'Generation contradiction does not authorize an edge mutation until historical ancestry provenance proves exactly one uncontested same-graph descendant endpoint.',
        );
      }

      return existingTreeRepairEligibilityDecision(
        input.defectClass,
        'REPAIR_PLANNING_ELIGIBLE',
        'TOPOLOGY',
        true,
        'Historical ancestry provenance proves exactly one endpoint. A successor may plan the topology correction but may not execute it from this authority.',
      );

    case 'PARTNERSHIP_PRESENTATION_CONTRADICTION':
      if (
        input.storedPartnershipReciprocal &&
        input.projectionOrRendererFailureProven
      ) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'PRESENTATION_ONLY',
          'PRESENTATION',
          true,
          'Stored partnership topology is reciprocal. The proven defect belongs to projection or rendering and must not rewrite family relationship data.',
        );
      }

      if (input.storedPartnershipReciprocal) {
        return existingTreeRepairEligibilityDecision(
          input.defectClass,
          'NO_STORED_TOPOLOGY_REPAIR',
          'NONE',
          false,
          'Stored partnership topology is reciprocal and no current projection/renderer failure is proven. No stored relationship repair is authorized.',
        );
      }

      return existingTreeRepairEligibilityDecision(
        input.defectClass,
        'EVIDENCE_REQUIRED',
        'TOPOLOGY',
        false,
        'Stored partnership reciprocity is not proven, so presentation-only classification would be unsafe.',
      );
  }
}

export interface ExistingTreeRepairEligibilitySummary {
  readonly decisionCount:
    number;

  readonly repairPlanningEligibleCount:
    number;

  readonly evidenceRequiredCount:
    number;

  readonly presentationOnlyCount:
    number;

  readonly noStoredTopologyRepairCount:
    number;

  readonly executionAllowed:
    false;

  readonly productionWritesAllowed:
    false;

  readonly automaticRepairAllowed:
    false;
}

export function summarizeExistingTreeRepairEligibility(
  decisions:
    readonly ExistingTreeRepairEligibilityDecision[],
): ExistingTreeRepairEligibilitySummary {
  return Object.freeze({
    decisionCount:
      decisions.length,

    repairPlanningEligibleCount:
      decisions.filter(
        decision =>
          decision.repairPlanningEligible,
      ).length,

    evidenceRequiredCount:
      decisions.filter(
        decision =>
          decision.status ===
            'EVIDENCE_REQUIRED',
      ).length,

    presentationOnlyCount:
      decisions.filter(
        decision =>
          decision.status ===
            'PRESENTATION_ONLY',
      ).length,

    noStoredTopologyRepairCount:
      decisions.filter(
        decision =>
          decision.status ===
            'NO_STORED_TOPOLOGY_REPAIR',
      ).length,

    executionAllowed: false,
    productionWritesAllowed: false,
    automaticRepairAllowed: false,
  });
}
/*
 * CC-TREE-LIVE-003G3M
 *
 * EXISTING_TREE_REPAIR_EXECUTION_DESIGN_AUTHORITY
 *
 * 003G3M designs successor server transactions. It does not install or invoke
 * them and grants zero production execution authority.
 *
 * Existing claim and parent-edge barriers remain fail-closed:
 *
 * - ordinary claim release/rebind stays prohibited;
 * - the existing ancestry writer stays additive-only;
 * - execution requires a separate future migration and activation milestone.
 */
export type ExistingTreeExecutionDesignClass =
  | 'CANONICAL_CLAIM_REBIND'
  | 'ANCESTRY_EDGE_RELOCATION';

export type ExistingTreeExecutionDesignStatus =
  | 'DESIGN_READY'
  | 'BLOCKED_ELIGIBILITY'
  | 'BLOCKED_OWNER_AUTHORITY'
  | 'BLOCKED_CLAIMANT_CONSENT'
  | 'BLOCKED_IDENTITY_STATE'
  | 'BLOCKED_DEPENDENCIES'
  | 'BLOCKED_PROVENANCE'
  | 'BLOCKED_STALE_STATE'
  | 'BLOCKED_TOPOLOGY_SAFETY';

export interface CanonicalClaimRebindExecutionDesignInput {
  readonly eligibilityStatus:
    ExistingTreeRepairEligibilityStatus;

  readonly sameGraphProven:
    boolean;

  readonly treeOwnerRequestRequired:
    true;

  readonly treeOwnerRequestPresent:
    boolean;

  readonly claimantConsentRequired:
    true;

  readonly claimantConsentPresent:
    boolean;

  readonly claimantMatchesCurrentSourceClaim:
    boolean;

  readonly sourceClaimedAndUnattached:
    boolean;

  readonly targetUnclaimedAndStructurallyConnected:
    boolean;

  readonly sourceDependencyCount:
    number;

  readonly uniqueClaimIndexProven:
    boolean;

  readonly claimTransitionBarrierProven:
    boolean;
}

export interface AncestryEdgeRelocationExecutionDesignInput {
  readonly eligibilityStatus:
    ExistingTreeRepairEligibilityStatus;

  readonly sameGraphProven:
    boolean;

  readonly ownerAuthorityProven:
    boolean;

  readonly provenanceStatus:
    CanonicalTreeLegacyGenerationProvenanceStatus;

  readonly sourceEdgePresent:
    boolean;

  readonly targetEdgeAbsent:
    boolean;

  readonly exactBeforeStatePresent:
    boolean;

  readonly expectedAuthoritySnapshotPresent:
    boolean;

  readonly edgeCountPreserved:
    boolean;

  readonly postStateCycleFree:
    boolean;

  readonly postStateCrossGraphCount:
    number;

  readonly postStateDanglingCount:
    number;

  readonly postStateSelfParentCount:
    number;

  readonly generationContradictionResolved:
    boolean;
}

export interface ExistingTreeExecutionDesignDecision {
  readonly designClass:
    ExistingTreeExecutionDesignClass;

  readonly status:
    ExistingTreeExecutionDesignStatus;

  readonly designReady:
    boolean;

  readonly migrationRequired:
    true;

  readonly installationAuthorized:
    false;

  readonly executionAllowed:
    false;

  readonly productionWritesAllowed:
    false;

  readonly automaticRepairAllowed:
    false;

  readonly preservesExistingClaimBarrier:
    true;

  readonly preservesExistingAdditiveAncestryWriter:
    true;

  readonly requiresDurableAuditEvidence:
    true;

  readonly requiresAtomicPostWriteVerification:
    true;

  readonly reason:
    string;
}

const existingTreeExecutionDesignDecision = (
  designClass:
    ExistingTreeExecutionDesignClass,
  status:
    ExistingTreeExecutionDesignStatus,
  reason:
    string,
): ExistingTreeExecutionDesignDecision =>
  Object.freeze({
    designClass,
    status,
    designReady:
      status === 'DESIGN_READY',
    migrationRequired: true,
    installationAuthorized: false,
    executionAllowed: false,
    productionWritesAllowed: false,
    automaticRepairAllowed: false,
    preservesExistingClaimBarrier: true,
    preservesExistingAdditiveAncestryWriter: true,
    requiresDurableAuditEvidence: true,
    requiresAtomicPostWriteVerification: true,
    reason,
  });

export function designCanonicalClaimRebindExecution(
  input:
    CanonicalClaimRebindExecutionDesignInput,
): ExistingTreeExecutionDesignDecision {
  if (
    input.eligibilityStatus !==
      'REPAIR_PLANNING_ELIGIBLE' ||
    !input.sameGraphProven
  ) {
    return existingTreeExecutionDesignDecision(
      'CANONICAL_CLAIM_REBIND',
      'BLOCKED_ELIGIBILITY',
      'Canonical rebind design requires prior 003G3L repair-planning eligibility in the exact same graph.',
    );
  }

  if (
    !input.treeOwnerRequestPresent
  ) {
    return existingTreeExecutionDesignDecision(
      'CANONICAL_CLAIM_REBIND',
      'BLOCKED_OWNER_AUTHORITY',
      'The Tree owner must first request the exact canonical target and currently claimed source pair.',
    );
  }

  if (
    !input.claimantConsentPresent
  ) {
    return existingTreeExecutionDesignDecision(
      'CANONICAL_CLAIM_REBIND',
      'BLOCKED_CLAIMANT_CONSENT',
      'The currently bound member must explicitly consent while authenticated. Owner or service-role authority cannot substitute for member identity consent.',
    );
  }

  if (
    !input.claimantMatchesCurrentSourceClaim ||
    !input.sourceClaimedAndUnattached ||
    !input.targetUnclaimedAndStructurallyConnected ||
    !input.uniqueClaimIndexProven ||
    !input.claimTransitionBarrierProven
  ) {
    return existingTreeExecutionDesignDecision(
      'CANONICAL_CLAIM_REBIND',
      'BLOCKED_IDENTITY_STATE',
      'Claimant, source claim, canonical target, unique-claim invariant, and transition-barrier state must all match the certified request before a rebind transaction can be designed for execution.',
    );
  }

  if (
    !Number.isInteger(
      input.sourceDependencyCount,
    ) ||
    input.sourceDependencyCount !== 0
  ) {
    return existingTreeExecutionDesignDecision(
      'CANONICAL_CLAIM_REBIND',
      'BLOCKED_DEPENDENCIES',
      'The redundant claimed source must be dependency-free or a separately governed dependency migration is required.',
    );
  }

  return existingTreeExecutionDesignDecision(
    'CANONICAL_CLAIM_REBIND',
    'DESIGN_READY',
    'A successor may install a two-phase owner-request/member-consent transaction that archives old claim evidence, clears the redundant claim under a repair-only transition mode, binds the exact canonical target, retires the redundant row, writes durable audit evidence, and verifies post-state atomically.',
  );
}

export function designAncestryEdgeRelocationExecution(
  input:
    AncestryEdgeRelocationExecutionDesignInput,
): ExistingTreeExecutionDesignDecision {
  if (
    input.eligibilityStatus !==
      'REPAIR_PLANNING_ELIGIBLE' ||
    !input.sameGraphProven
  ) {
    return existingTreeExecutionDesignDecision(
      'ANCESTRY_EDGE_RELOCATION',
      'BLOCKED_ELIGIBILITY',
      'An ancestry relocation requires prior 003G3L repair-planning eligibility in one proven graph.',
    );
  }

  if (!input.ownerAuthorityProven) {
    return existingTreeExecutionDesignDecision(
      'ANCESTRY_EDGE_RELOCATION',
      'BLOCKED_OWNER_AUTHORITY',
      'Tree-owner mutation authority must be proven before topology relocation is eligible for successor execution.',
    );
  }

  if (
    input.provenanceStatus !==
      'PROVEN_UNAMBIGUOUS_ENDPOINT'
  ) {
    return existingTreeExecutionDesignDecision(
      'ANCESTRY_EDGE_RELOCATION',
      'BLOCKED_PROVENANCE',
      'A structurally plausible post-state is not ancestry authority. The exact destination endpoint must be proven by governed provenance before execution.',
    );
  }

  if (
    !input.sourceEdgePresent ||
    !input.targetEdgeAbsent ||
    !input.exactBeforeStatePresent ||
    !input.expectedAuthoritySnapshotPresent
  ) {
    return existingTreeExecutionDesignDecision(
      'ANCESTRY_EDGE_RELOCATION',
      'BLOCKED_STALE_STATE',
      'The exact source edge, absent destination edge, row before-state, and graph authority snapshot must all match at execution time.',
    );
  }

  if (
    !input.edgeCountPreserved ||
    !input.postStateCycleFree ||
    input.postStateCrossGraphCount !== 0 ||
    input.postStateDanglingCount !== 0 ||
    input.postStateSelfParentCount !== 0 ||
    !input.generationContradictionResolved
  ) {
    return existingTreeExecutionDesignDecision(
      'ANCESTRY_EDGE_RELOCATION',
      'BLOCKED_TOPOLOGY_SAFETY',
      'The proposed relocation must preserve edge cardinality and resolve the contradiction without cycles, cross-graph references, dangling references, or self-parent edges.',
    );
  }

  return existingTreeExecutionDesignDecision(
    'ANCESTRY_EDGE_RELOCATION',
    'DESIGN_READY',
    'A successor may install a dedicated atomic relocation writer that removes one proven source parent edge and adds the same parent to one proven destination endpoint under graph-wide stale-state protection and atomic post-state certification.',
  );
}

export interface ExistingTreeExecutionTransactionContract {
  readonly canonicalClaimRebind:
    Readonly<{
      phaseCount: 2;
      ownerRequestDurable: true;
      claimantConsentDurable: true;
      serviceRoleMaySubstituteForConsent: false;
      oldClaimEvidenceArchivedBeforeRelease: true;
      repairOnlyClaimTransitionModeRequired: true;
      uniqueClaimInvariantPreserved: true;
      redundantRowRetirementRequired: true;
      dependencyMigrationImplicitlyAllowed: false;
    }>;

  readonly ancestryEdgeRelocation:
    Readonly<{
      existingAdditiveWriterReused: false;
      dedicatedRelocationWriterRequired: true;
      sourceRemovalAndDestinationAddAtomic: true;
      graphAdvisoryLockRequired: true;
      deterministicRowLocksRequired: true;
      exactBeforeStateRequired: true;
      exactAuthoritySnapshotRequired: true;
      provenanceGateRequired: true;
      cycleCheckRequired: true;
      crossGraphCheckRequired: true;
      edgeCountPreservationRequired: true;
    }>;

  readonly productionInstallationAuthorized:
    false;

  readonly productionExecutionAuthorized:
    false;
}

export const EXISTING_TREE_EXECUTION_TRANSACTION_CONTRACT:
  ExistingTreeExecutionTransactionContract =
  Object.freeze({
    canonicalClaimRebind:
      Object.freeze({
        phaseCount: 2,
        ownerRequestDurable: true,
        claimantConsentDurable: true,
        serviceRoleMaySubstituteForConsent: false,
        oldClaimEvidenceArchivedBeforeRelease: true,
        repairOnlyClaimTransitionModeRequired: true,
        uniqueClaimInvariantPreserved: true,
        redundantRowRetirementRequired: true,
        dependencyMigrationImplicitlyAllowed: false,
      }),

    ancestryEdgeRelocation:
      Object.freeze({
        existingAdditiveWriterReused: false,
        dedicatedRelocationWriterRequired: true,
        sourceRemovalAndDestinationAddAtomic: true,
        graphAdvisoryLockRequired: true,
        deterministicRowLocksRequired: true,
        exactBeforeStateRequired: true,
        exactAuthoritySnapshotRequired: true,
        provenanceGateRequired: true,
        cycleCheckRequired: true,
        crossGraphCheckRequired: true,
        edgeCountPreservationRequired: true,
      }),

    productionInstallationAuthorized: false,
    productionExecutionAuthorized: false,
  });
